<template>
  <el-dialog
  class="multi-language-dialog"
  :class="{'fullscreen':fullscreen}"
    :title="title"
    :visible.sync="visible"
    :width="width"
    :top="top"
    :modal="modal"
    :lock-scroll="lockScroll"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :show-close="showClose"
    :before-close="handleBeforeClose"
    :center="center"
    :custom-class="customClass"
    :fullscreen="fullscreen"
    @open="handleOpen"
    @opened="handleOpened"
    @close="handleClose"
    @closed="handleClosed"
  >
    <!-- 头部插槽 -->
    <template v-if="$slots.title" slot="title">
      <slot name="title"></slot>
    </template>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" :element-loading-text="loadingText">
    </div>

    <!-- 选项卡模式 -->
    <div v-else-if="internalIsTabMode && internalTabsData.length > 0" class="dialog-content">
      <slot name="before" :data="currentData"></slot>

      <!-- 多语言选项卡 -->
      <el-tabs
        v-if="enableMultiLanguage && languageList.length > 0"
        type="card"
        v-model="currentLanguageCode"
        @tab-click="handleLanguageTabClick"
        style="margin-bottom: 20px;"
      >
        <el-tab-pane
          v-for="(item, index) in languageList"
          :key="index"
          :label="item.name"
          :name="item.code"
        ></el-tab-pane>
      </el-tabs>

      <el-tabs v-model="internalActiveTab">
        <el-tab-pane
          v-for="tab in internalTabsData"
          :key="tab.name"
          :label="tab.label"
          :name="tab.name"
        >
          <DetailContent
            :data="tab.data"
            :columns="tab.columns"
            :column="column"
            :border="border"
            :size="size"
          >
            <!-- 传递所有插槽到内部组件 -->
            <template v-for="(slot, name) in $scopedSlots" :slot="name" slot-scope="scope">
              <slot :name="name" v-bind="scope"></slot>
            </template>
          </DetailContent>
        </el-tab-pane>
      </el-tabs>

      <slot name="after" :data="currentData"></slot>
    </div>

    <!-- 普通模式 -->
    <div v-else-if="currentData && Object.keys(currentData).length > 0" class="dialog-content">
      <slot name="before" :data="currentData"></slot>

      <!-- 多语言选项卡 -->
      <el-tabs
        v-if="enableMultiLanguage && languageList.length > 0"
        type="card"
        v-model="currentLanguageCode"
        @tab-click="handleLanguageTabClick"
        style="margin-bottom: 20px;"
      >
        <el-tab-pane
          v-for="(item, index) in languageList"
          :key="index"
          :label="item.name"
          :name="item.code"
        ></el-tab-pane>
      </el-tabs>

      <DetailContent
        :data="currentData"
        :columns="currentColumns"
        :column="column"
        :border="border"
        :size="size"
      >
        <!-- 传递所有插槽到内部组件 -->
        <template v-for="(slot, name) in $scopedSlots" :slot="name" slot-scope="scope">
          <slot :name="name" v-bind="scope"></slot>
        </template>
      </DetailContent>

      <slot name="after" :data="currentData"></slot>
    </div>

    <!-- 无数据状态 -->
    <div v-else class="no-data">
      <div class="empty-content">
        <i class="el-icon-document" style="font-size: 48px; color: #C0C4CC;"></i>
        <p style="margin: 10px 0; color: #909399;">{{ emptyText }}</p>
      </div>
    </div>

    <!-- 底部插槽 -->
    <template slot="footer">
      <slot name="footer" :data="currentData">
        <el-button @click="handleClose">{{ closeText }}</el-button>
      </slot>
    </template>
  </el-dialog>
</template>

<script>
import DetailContent from './DetailContent.vue';

export default {
  name: 'MultiLanguageDetailDialog',
  components: {
    DetailContent
  },
  props: {
    // 弹窗基础配置
    title: { type: String, default: '详情' },
    width: { type: String, default: '800px' },
    top: { type: String, default: '15vh' },
    modal: { type: Boolean, default: true },
    lockScroll: { type: Boolean, default: true },
    closeOnClickModal: { type: Boolean, default: true },
    closeOnPressEscape: { type: Boolean, default: true },
    showClose: { type: Boolean, default: true },
    center: { type: Boolean, default: false },
    customClass: { type: String, default: '' },
    closeText: { type: String, default: '关闭' },
    emptyText: { type: String, default: '暂无数据' },
    loadingText: { type: String, default: '加载中...' },
    fullscreen: { type: Boolean, default: false },
    
    // 数据配置
    data: { type: Object, default: () => ({}) },
    columns: { type: Array, default: () => [] },
    column: { type: Number, default: 2 },
    border: { type: Boolean, default: true },
    size: { type: String, default: 'medium' },
    
    // 接口配置
    api: { type: String, default: '' },
    params: { type: Object, default: () => ({}) },
    method: { type: String, default: 'get' },
    dataFormat: { type: Function, default: null },
    
    // 多语言详情接口配置
    enableMultiLanguage: { type: Boolean, default: true },
    detailApi: { type: String, default: '' },
    detailApiTemplate: { type: String, default: '' },
    answerApi: { type: String, default: '' },
  },
  data() {
    return {
      visible: false,
      loading: false,
      currentData: {},
      currentColumns: [],
      internalIsTabMode: false,
      internalTabsData: [],
      internalActiveTab: '',
      languageList: [],
      currentLanguageCode: '',
      // 多语言数据相关
      originalData: {},
      answerFormatFunction: null
    };
  },
  watch: {
    data: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.currentData = newVal;
        }
      },
      immediate: true,
      deep: true
    },
    columns: {
      handler(newVal) {
        this.currentColumns = newVal || [];
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async getLanguageList() {
      try {
        const { data: res } = await this.$post('/language/list', {
          adminStatus: 1
        });
        
        if (res && res.data) {
          this.languageList = res.data;
          this.currentLanguageCode = (res.data[0] && res.data[0].code) || 'EN';
        }
      } catch (error) {
        console.error('获取语言列表失败:', error);
      }
    },
    // 打开多语言详情（选项卡模式）
    async openWithMultiLanguage(tabsData = [], options = {}) {
      
      this.visible = true;
      this.internalIsTabMode = true;
      this.internalTabsData = tabsData;
      this.internalActiveTab = tabsData.length > 0 ? tabsData[0].name : '';
      this.currentData = {};
      this.currentColumns = [];
      this.loading = true;
      // 多语言支持
      if(this.languageList.length === 0){
        await this.getLanguageList()
      }
      
      // 保存详情接口信息，用于语言切换
      this.originalData = options.originalData || {};
      this.answerFormatFunction = options.answerFormatFunction || null;
      
      console.log('原始数据:', this.originalData);
      
      // 选项卡模式下，将第一个选项卡的数据作为当前数据（用于插槽）
      if (tabsData.length > 0) {
        this.currentData = tabsData[0].data || {};
      }
      
      // 如果启用多语言且有详情接口配置，自动加载默认语言的数据
      if (this.enableMultiLanguage && (this.detailApi || this.detailApiTemplate) && this.originalData.id) {
        console.log('自动加载默认语言数据');
        await this.loadLanguageDetailData(this.currentLanguageCode);
      } else {
        // console.log('不加载默认语言数据，原因:');
        // console.log('- enableMultiLanguage:', this.enableMultiLanguage);
        // console.log('- detailApi:', this.detailApi);
        // console.log('- detailApiTemplate:', this.detailApiTemplate);
        // console.log('- originalData.id:', this.originalData.id);
        
        // 显示loading效果
        this.loading = false;
        // 短暂的loading效果
        // setTimeout(() => {
        //   this.loading = false;
        // }, 300);
      }
    },

    // 处理语言选项卡点击事件
    async handleLanguageTabClick(tab) {
      console.log('切换到语言:', tab.name);
      this.currentLanguageCode = tab.name;
      
      // 如果有详情接口和原始数据，重新获取该语言的数据
      if ((this.detailApi || this.detailApiTemplate) && this.originalData.id) {
        await this.loadLanguageDetailData(tab.name);
      }
    },

    // 加载指定语言的详情数据
    async loadLanguageDetailData(languageCode) {
      if (!this.originalData.id) return;
      
      this.loading = true;
      try {
        // 构建详情接口URL
        let detailUrl = this.detailApi;
        if (this.detailApiTemplate) {
          detailUrl = this.detailApiTemplate.replace('{id}', this.originalData.id);
        } else if (this.detailApi) {
          detailUrl = `${this.detailApi}/${this.originalData.id}`;
        }

        console.log('详情接口URL:', detailUrl, '语言代码:', languageCode);

        // 获取该语言的详情数据
        let detailData = { ...this.originalData };
        if (detailUrl) {
          try {
            const { data: detailRes } = await this.$get(detailUrl, {
              headers: {
                'Language-Code': languageCode
              }
            });

            console.log('详情接口返回:', detailRes);

            if (detailRes && detailRes.data) {
              detailData = {
                ...this.originalData,
                ...detailRes.data
              };
            }
          } catch (error) {
            console.warn('获取详情失败，使用原始数据:', error);
          }
        }

        // 获取该语言的答案数据（如果配置了答案接口）
        let answers = [];
        if (this.answerApi) {
          try {
            console.log('答案接口URL:', this.answerApi, '题目ID:', this.originalData.id);
            
            const { data: answerRes } = await this.$post(this.answerApi, {
              questionId: this.originalData.id,
              pageNo: 1,
              pageSize: 1000
            }, {
              headers: {
                'Language-Code': languageCode
              }
            });

            console.log('答案接口返回:', answerRes);

            answers = answerRes && answerRes.data ? answerRes.data : [];
          } catch (error) {
            console.warn('获取答案失败:', error);
          }
        }

        console.log('最终详情数据:', detailData);
        console.log('答案数据:', answers);

        // 更新选项卡数据
        this.internalTabsData = this.internalTabsData.map(tab => {
          if (tab.name === 'basic') {
            return {
              ...tab,
              data: detailData
            };
          } else if (tab.name === 'answers') {
            return {
              ...tab,
              data: answers.length > 0 ? {
                summary: `共有 ${answers.length} 个答案，其中 ${answers.filter(a => a.isRight === 1).length} 个正确答案`,
                answersList: this.answerFormatFunction ? this.answerFormatFunction(answers) : this.formatAnswersListHTML(answers)
              } : {
                message: '暂无答案数据'
              }
            };
          }
          return tab;
        });

        // 更新当前数据
        this.currentData = detailData;

        console.log('更新后的选项卡数据:', this.internalTabsData);

      } catch (error) {
        console.error('获取语言详情数据失败:', error);
        this.$message.error('获取语言详情数据失败');
      } finally {
        this.loading = false;
      }
    },

    // 默认的答案列表格式化函数
    formatAnswersListHTML(answers) {
      if (!answers || answers.length === 0) {
        return '<div style="text-align: center; color: #999; padding: 20px;">暂无答案数据</div>';
      }
      
      return answers.map((answer, index) => {
        const isCorrect = answer.isRight === 1;
        const statusColor = isCorrect ? '#67C23A' : '#F56C6C';
        const statusText = isCorrect ? '正确答案' : '错误答案';
        const iconHtml = answer.icon ? `<img src="${answer.icon}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px; margin-right: 10px;" alt="答案图标">` : '';
        
        return `
          <div style="border: 1px solid #EBEEF5; border-radius: 4px; padding: 15px; margin-bottom: 10px; background: #F9F9F9;">
            <div style="display: flex; align-items: flex-start; margin-bottom: 10px;">
              ${iconHtml}
              <div style="flex: 1;">
                <div style="font-weight: bold; font-size: 14px; margin-bottom: 5px;">
                  ${answer.name || '未命名'}
                </div>
                <div style="color: #666; font-size: 12px; margin-bottom: 5px;">
                  ID: ${answer.id} | 排序: ${answer.sortOrder || 0}
                </div>
                <div style="color: #666; font-size: 12px;">
                  ${answer.description || '暂无描述'}
                </div>
              </div>
              <div style="text-align: right;">
                <span style="display: inline-block; padding: 2px 8px; border-radius: 4px; color: white; font-size: 12px; background-color: ${statusColor};">
                  ${statusText}
                </span>
                <div style="color: #999; font-size: 11px; margin-top: 5px;">
                  ${answer.isOffline === 0 ? '上架' : '下架'}
                </div>
              </div>
            </div>
            <div style="font-size: 11px; color: #999; text-align: right;">
              创建: ${this.formatDate(answer.ctime)} | 更新: ${this.formatDate(answer.utime)}
            </div>
          </div>
        `;
      }).join('');
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString();
    },

    // 其他方法保持与原DetailDialog一致
    open(data = null, columns = null, options = {}) {
      this.visible = true;
      this.internalIsTabMode = false;
      this.internalTabsData = [];
      this.internalActiveTab = '';
      
      if (data) {
        this.loading = true;
        this.currentData = data;
        this.currentColumns = columns || this.columns;
        setTimeout(() => {
          this.loading = false;
        }, 300);
      } else if (columns) {
        this.currentColumns = columns;
      }
      
      if (this.api || options.api) {
        this.fetchData(options.api, options.params, options.method);
      }
    },

    openWithTabs(tabsData = [], options = {}) {
      this.openWithMultiLanguage(tabsData, options);
    },

    close() {
      this.visible = false;
    },

    async fetchData(api = null, params = null, method = null, headers = {}) {
      const apiUrl = api || this.api;
      const apiParams = params !== null ? params : this.params;
      const apiMethod = method || this.method;
      
      if (!apiUrl) return;

      this.loading = true;
      try {
        let response;
        const requestMethod = apiMethod.toLowerCase();
        const requestOptions = headers && Object.keys(headers).length > 0 ? { headers } : {};
        
        if (requestMethod === 'post') {
          response = await this.$post(apiUrl, apiParams, requestOptions);
        } else if (requestMethod === 'put') {
          response = await this.$put(apiUrl, apiParams, requestOptions);
        } else if (requestMethod === 'delete') {
          response = await this.$delete(apiUrl, apiParams, requestOptions);
        } else {
          response = await this.$get(apiUrl, { params: apiParams, ...requestOptions });
        }

        let data = response.data;
        
        if (this.dataFormat && typeof this.dataFormat === 'function') {
          data = this.dataFormat(response);
        } else if (data && data.data) {
          data = data.data;
        }

        this.currentData = data || {};
        
        this.$emit('data-loaded', this.currentData);
      } catch (error) {
        console.error('获取详情数据失败:', error);
        this.$message.error('获取详情数据失败');
        this.currentData = {};
        this.$emit('data-error', error);
      } finally {
        this.loading = false;
      }
    },

    refresh() {
      if (this.api) {
        this.fetchData();
      }
    },

    handleBeforeClose(done) {
      this.$emit('before-close', done);
      if (this.$listeners['before-close']) {
        return;
      }
      done();
    },

    handleOpen() {
      this.$emit('open');
    },

    handleOpened() {
      this.$emit('opened');
    },

    handleClose() {
      this.visible = false;
      this.$emit('close');
    },

    handleClosed() {
      this.currentData = {};
      this.internalIsTabMode = false;
      this.internalTabsData = [];
      this.internalActiveTab = '';
      // this.enableMultiLanguage = false;
      // this.languageList = [];
      this.currentLanguageCode = 'EN';
      this.$emit('closed');
    },
    async openTabsByApi(api, params = {}, tabsConfig = [], options = {}) {
      this.visible = true;
      this.internalIsTabMode = true;
      this.loading = true;

      
      // 处理选项
      if (options.title) this.title = options.title;
      if (options.width) this.width = options.width;
      if (options.originalData) this.originalData = options.originalData;
      
      try {
        const { data: res } = await this.$get(api, { params });
        
        let responseData = res;
        if (res && res.data) {
          responseData = res.data;
        }
        
        // 根据配置生成选项卡数据
        this.internalTabsData = tabsConfig.map(config => {
          let tabData = responseData;
          
          // 如果有预处理函数，使用它处理数据
          if (config.preProcessData && typeof config.preProcessData === 'function') {
            tabData = config.preProcessData(responseData);
          }
          
          return {
            label: config.label,
            name: config.name,
            columns: config.columns,
            data: tabData
          };
        });
        
        this.internalActiveTab = this.internalTabsData.length > 0 ? this.internalTabsData[0].name : '';
        
        // 设置当前数据（用于插槽）
        if (this.internalTabsData.length > 0) {
          this.currentData = this.internalTabsData[0].data || {};
        }
        
      } catch (error) {
        console.error('获取详情数据失败:', error);
        this.$message.error('获取详情数据失败');
        this.internalTabsData = [];
        this.currentData = {};
      } finally {
        this.loading = false;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}

.loading-container {
  position: relative;
  height: 200px;
}

.no-data {
  text-align: center;
  padding: 40px 0;
  
  .empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    i {
      margin-bottom: 16px;
    }
    
    p {
      font-size: 14px;
      line-height: 1.5;
    }
  }
}

::v-deep .el-dialog__body {
  padding: 20px;
}
.multi-language-dialog{

:deep(.el-dialog){
  margin:0 auto !important;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  .el-dialog__header,.el-dialog__footer{
    flex:none;
    width: 100%;
    box-sizing: border-box;
  }
  .el-dialog__body{
    flex: 1 1 auto;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
  }
}
&.fullscreen{
    :deep(.el-dialog){
      height: 100vh;
      width: 100vw;
    }
}
}
</style> 